import * as React from 'react';
import { Trans, useLingui } from '@lingui/react';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { DateInput } from '@startuptools/ui/DateInput';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useUpdateWarrantProgramDatesMutation } from './StepDates.graphql';
import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { Box } from '@startuptools/ui/Box';
import { DateTime } from 'luxon';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Select } from '@startuptools/ui/Select';
import { Option } from '@startuptools/ui/Option';
import { Button } from '@startuptools/ui/Button';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { Link } from '@startuptools/ui/Link';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';
import { calculatedDefaultDuration, createDurationArray, DURATION_RANGE } from '../../utils/create-duration-array';
import { GqlWarrantProgramKind } from '../../../../../graphql/react-operations';
import { Alert } from '@startuptools/ui/Alert';
import { captureException } from '@sentry/react';
import { toast } from '@startuptools/ui/toast/use-toast';
import { DateFormat } from '@startuptools/ui/DateFormat';
import {
  GqlCompany,
  GqlMeeting,
  GqlMeetingConcern,
  GqlWarrantProgram,
} from '../../../../../graphql/base-types.graphql';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';

type FormValues = {
  warrantProgramStartDate: DateTime;
  duration: number;
  warrantSubscribeEndDate: DateTime | undefined;
  payBeforeDate: DateTime | undefined;
  shareSubscribeStartDate: DateTime | undefined;
  shareSubscribeEndDate: DateTime;
};

const qesoMinDate = DateTime.fromObject({
  year: 2018,
  month: 1,
  day: 1,
}).startOf('day');

const createDefaultValues = (
  warrantProgram: Pick<
    GqlWarrantProgram,
    | 'warrantKind'
    | 'warrantStartDate'
    | 'warrantSubscribeEndDate'
    | 'payBeforeDate'
    | 'shareSubscribeStartDate'
    | 'shareSubscribeEndDate'
  > & { meetingConcern?: (Pick<GqlMeetingConcern, 'id'> & { meeting: Pick<GqlMeeting, 'startTime'> }) | null },
): FormValues => {
  const warrantProgramStartDate = warrantProgram?.warrantStartDate
    ? DateTime.fromISO(warrantProgram.warrantStartDate)
    : warrantProgram.meetingConcern?.meeting.startTime
      ? DateTime.fromISO(warrantProgram.meetingConcern.meeting.startTime).startOf('day')
      : DateTime.now().startOf('day');

  const warrantSubscribeEndDate = warrantProgram?.warrantSubscribeEndDate
    ? DateTime.fromISO(warrantProgram.warrantSubscribeEndDate)
    : warrantProgramStartDate;

  const payBeforeDate = warrantProgram?.payBeforeDate
    ? DateTime.fromISO(warrantProgram.payBeforeDate)
    : warrantSubscribeEndDate.plus({ months: 1 });

  const duration = calculatedDefaultDuration(warrantProgram);

  const shareSubscribeStartDate = warrantProgram?.shareSubscribeStartDate
    ? DateTime.fromISO(warrantProgram.shareSubscribeStartDate)
    : warrantProgramStartDate.plus({ months: duration, days: 1 });

  const shareSubscribeEndDate = warrantProgram?.shareSubscribeEndDate
    ? DateTime.fromISO(warrantProgram.shareSubscribeEndDate)
    : shareSubscribeStartDate.plus({ months: 1 });
  return {
    warrantProgramStartDate,
    duration,
    warrantSubscribeEndDate,
    payBeforeDate,
    shareSubscribeStartDate,
    shareSubscribeEndDate,
  };
};

export const WpStepDates = ({
  warrantProgram,
}: {
  warrantProgram: Pick<
    GqlWarrantProgram,
    | 'id'
    | 'companyId'
    | 'warrantKind'
    | 'warrantStartDate'
    | 'warrantEndDate'
    | 'warrantSubscribeEndDate'
    | 'payBeforeDate'
    | 'shareSubscribeStartDate'
    | 'shareSubscribeEndDate'
  > & { company: Pick<GqlCompany, 'foundationDate'> } & {
    meetingConcern?: (Pick<GqlMeetingConcern, 'id'> & { meeting: Pick<GqlMeeting, 'startTime'> }) | null;
  };
}) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  const [updateWarrantProgramDates, { loading }] = useUpdateWarrantProgramDatesMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });

  const durationMin = DURATION_RANGE[warrantProgram?.warrantKind ?? GqlWarrantProgramKind.Employee].min;
  const durationMax = DURATION_RANGE[warrantProgram?.warrantKind ?? GqlWarrantProgramKind.Employee].max;

  const form = useForm<FormValues>({
    defaultValues: createDefaultValues(warrantProgram),
  });

  React.useEffect(() => {
    if (warrantProgram.meetingConcern?.meeting.startTime && !form.formState.isDirty) {
      form.reset(createDefaultValues(warrantProgram));
    }
  }, [warrantProgram.meetingConcern?.meeting.startTime, form, warrantProgram]);

  const currentStartDate = form.watch('warrantProgramStartDate');
  const currentWarrantSubscribeEndDate = form.watch('warrantSubscribeEndDate');
  const currentShareSubscribeStartDate = form.watch('shareSubscribeStartDate');

  const currentDurationOptions = createDurationArray(durationMin, durationMax, false, 0, currentStartDate);

  const meeting = warrantProgram.meetingConcern?.meeting;

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      await updateWarrantProgramDates({
        variables: {
          companyId,
          input: {
            id: warrantProgram.id,
            warrantStartDate: values.warrantProgramStartDate.toISODate() ?? '',
            duration: values.duration,
            warrantSubscribeEndDate: values.warrantSubscribeEndDate?.toISODate(),
            payBeforeDate: values.payBeforeDate?.toISODate(),
            shareSubscribeStartDate: values.shareSubscribeStartDate?.toISODate(),
            shareSubscribeEndDate: values.shareSubscribeEndDate.toISODate() ?? '',
          },
        },
      });
      return stepNext();
    } catch (error) {
      captureException(error);
      toast({
        title: <Trans id="Could not save" />,
        severity: 'danger',
        description: (
          <Trans id="Something wen wrong when saving, please try again and contact support if problem persists" />
        ),
      });
      return;
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Important dates" />
        </Typography>

        {warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant && (
          <Alert color="neutral">
            <Typography component="span">
              <Trans id="The longer the duration of the warrants, the longer time the company has to rise in value, which is good for the warrant holders. But a longer duration also means that the warrants become more expensive. Between 3 and 5 years is common. You can change the duration and see how that affects the price." />
            </Typography>
          </Alert>
        )}

        {warrantProgram.warrantKind === GqlWarrantProgramKind.Employee && (
          <Alert color="neutral">
            <Typography component="span">
              <Trans
                id="The duration of the options must be at least 3 years and no more than 10 years. As opposed to what is common when issuing warrants, no vesting can take place the during the first three years. For more information, see: <link>Link</link>"
                components={{
                  link: (
                    <Link
                      href="https://startuptools.org/se/vesting-de-forsta-tre-aren-problemen-med-personaloptioner-dev-4-av-4/"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </Typography>
          </Alert>
        )}

        <Box maxWidth={400}>
          <Controller
            control={form.control}
            name="warrantProgramStartDate"
            rules={{
              validate: {
                qesoMinDate: value => {
                  if (!value) return true;
                  if (warrantProgram.warrantKind !== GqlWarrantProgramKind.Employee) return true;
                  return (
                    value.startOf('day') >= qesoMinDate ||
                    i18n._('QESO programs cannot start before {date}', {
                      date: <DateFormat locale={i18n.locale}>{qesoMinDate}</DateFormat>,
                    })
                  );
                },
                notBeforeMeeting: value => {
                  if (!warrantProgram.meetingConcern?.meeting) return true;
                  if (!value) return true;
                  const meetingDate = DateTime.fromISO(warrantProgram.meetingConcern.meeting.startTime).startOf('day');

                  return (
                    value.startOf('day') >= meetingDate ||
                    i18n._('Vesting cannot start before the date of the decision')
                  );
                },
                notBeforeFoundation: value => {
                  if (!warrantProgram.company) return true;
                  if (!value) return true;
                  const foundationDate = DateTime.fromISO(warrantProgram.company.foundationDate).startOf('day');

                  return (
                    value.startOf('day') >= foundationDate ||
                    i18n._("Vesting cannot be before the company's foundation")
                  );
                },
              },
            }}
            render={({ field, formState }) => (
              <FormControl error={!!formState.errors.warrantProgramStartDate}>
                <FormLabel>
                  {warrantProgram.warrantKind === GqlWarrantProgramKind.Wise ? (
                    <Trans id="What should the starting date of the convertible be?" />
                  ) : (
                    <Trans id="What should the starting date of options be?" />
                  )}
                </FormLabel>
                <DateInput {...field} />
                {formState.errors.warrantProgramStartDate && (
                  <Typography color="danger" fontSize="sm">
                    {formState.errors.warrantProgramStartDate.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </Box>

        <Box maxWidth={400}>
          <Controller
            control={form.control}
            name="duration"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value, ...field } }) => (
              <FormControl>
                <FormLabel>
                  {warrantProgram.warrantKind === GqlWarrantProgramKind.Wise ? (
                    <Trans id="After how long time should the investors be able to subscribe for shares unless a new share issue has taken place before?" />
                  ) : (
                    <Trans id="After how long time should the subscribers be able to get their shares?" />
                  )}
                </FormLabel>
                <Select
                  key={currentStartDate.toISO()}
                  {...field}
                  value={value.toString()}
                  onChange={(_, newValue) => onChange(Number(newValue))}
                >
                  {currentDurationOptions.map(option => (
                    <Option key={option.value} value={option.value.toString()}>
                      {option.text}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Box>

        {(warrantProgram.warrantKind === GqlWarrantProgramKind.Wise ||
          warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant) && (
          <Box maxWidth={400}>
            <Controller
              control={form.control}
              name="warrantSubscribeEndDate"
              rules={{
                required: true,
                validate: value => {
                  if (value && value.startOf('day') > currentStartDate.startOf('day')) {
                    return i18n._('Cannot be after the program start');
                  }
                  if (
                    meeting?.startTime &&
                    value &&
                    value.startOf('day') < DateTime.fromISO(meeting.startTime).startOf('day')
                  ) {
                    return i18n._({
                      id: 'Cannot be before the meeting date {date}',
                      values: { date: DateTime.fromISO(meeting.startTime).toLocaleString(DateTime.DATE_SHORT) },
                    });
                  }
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl error>
                  <FormLabel>
                    <Trans id="Last day to subscribe for the warrants" />
                  </FormLabel>
                  <DateInput {...field} maxDate={currentStartDate} />
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Box>
        )}

        {(warrantProgram.warrantKind === GqlWarrantProgramKind.Wise ||
          warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant) && (
          <Box maxWidth={400}>
            <Controller
              control={form.control}
              name="payBeforeDate"
              rules={{
                required: true,
                validate: value => {
                  if (!value || !currentWarrantSubscribeEndDate) return true;
                  if (value.startOf('day') < currentWarrantSubscribeEndDate.startOf('day')) {
                    return i18n._('Cannot be before first warrant subscription date');
                  }
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl error>
                  <FormLabel>
                    <Trans id="Last day to pay for the options" />
                  </FormLabel>
                  <DateInput {...field} />
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Box>
        )}

        {(warrantProgram.warrantKind === GqlWarrantProgramKind.Employee ||
          warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant) && (
          <Box maxWidth={400}>
            <Controller
              control={form.control}
              name="shareSubscribeStartDate"
              rules={{
                required: true,
                validate: value => {
                  if (!value || !currentStartDate) return true;
                  if (value.startOf('day') < currentStartDate.startOf('day')) {
                    return i18n._('Cannot be before first warrant subscription date');
                  }
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl error>
                  <FormLabel>
                    <Trans id="First day to subscribe for shares" />
                  </FormLabel>
                  <DateInput {...field} onChange={newValue => field.onChange(newValue)} minDate={currentStartDate} />
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Box>
        )}

        {(warrantProgram.warrantKind === GqlWarrantProgramKind.Employee ||
          warrantProgram.warrantKind === GqlWarrantProgramKind.Wise ||
          warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant) && (
          <Box maxWidth={400}>
            <Controller
              key={currentShareSubscribeStartDate?.toISO()}
              control={form.control}
              name="shareSubscribeEndDate"
              rules={{
                required: true,
                validate: value => {
                  if (!value || !currentShareSubscribeStartDate) return true;
                  if (value.startOf('day') < currentShareSubscribeStartDate.startOf('day')) {
                    return i18n._('Cannot be before first share subscription date');
                  }
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl error>
                  <FormLabel>
                    <Trans id="Last day to subscribe for shares" />
                  </FormLabel>
                  <DateInput
                    {...field}
                    onChange={newValue => field.onChange(newValue)}
                    minDate={currentShareSubscribeStartDate}
                  />
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Box>
        )}

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
